import { createSelector } from '@reduxjs/toolkit';

import type { ProductPromptType } from '@/components/products/ProductsPromptModal/redux/types';
import openSpeedyFlow from '@/components/SpeedyFlow/openSpeedyFlow';
import { isEmbedded, loadPage } from '@/helpers';
import route from '@/lib/route';
import { AppState } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';
import { createSelectCanAccessHabitat } from '@/redux/selectors/users';
import store from '@/redux/store';

export const buildHabitatURL = (zooSlug: string, habitatSlug: string, query?: string | URLSearchParams) => {
  const params = query instanceof URLSearchParams ? query : new URLSearchParams(query);
  // Logged-out users get the special mobile timeline view on landing; default `timeline=0` to skip it.
  // if the link has `ppt` query param, it has the same function as `timeline=0` to skip the timeline.
  if (!store.getState().user.logged && !params.has('timeline') && !params.has('ppt')) {
    params.set('timeline', '0');
  }
  const queryString = params.toString();
  return `/h/${zooSlug}/${habitatSlug}${queryString ? `?${queryString}` : ''}`;
};

export const selectHabitatForProductsPromptModal = createSelector(
  createSortedHabitatsSelector(['online', 'lastMediaCapture', 'index', 'currentViewers', 'title']),
  (state: AppState) => state.user.subscription.habitatsAccessUntil,
  (habitats, habitatsAccessUntil) => {
    return habitats.find(
      (habitat) => !(habitatsAccessUntil?.[habitat._id] && Date.parse(habitatsAccessUntil[habitat._id]) > Date.now()),
    );
  },
);

export const routeToHabitat = (
  {
    id,
    slug,
    zooSlug,
    query,
  }: {
    id: string;
    slug: string;
    zooSlug: string;
    query?: string;
  },
  ppt?: ProductPromptType,
) => {
  const state = store.getState();
  const canAccessHabitat = createSelectCanAccessHabitat(id)(state);
  const isLoggedIn = state.user.logged;
  if (isLoggedIn && !canAccessHabitat) {
    openSpeedyFlow(state, store.dispatch, { kind: 'habitat-access', habitatId: id, ppt, entryPoint: 'habitat-link' });
    return;
  }
  const url = buildHabitatURL(zooSlug, slug, query);
  if (isEmbedded()) {
    loadPage(url);
    return;
  }
  route(url);
};
